import React, { useState, useEffect, useCallback } from 'react';
import { agentTokensApi, AgentTokenMetadata, AgentTokenCreateResponse } from '../../services/api';
import { GlassCard } from '../ui/GlassCard';
import { GlassButton } from '../ui/GlassButton';
import { GlassInput } from '../ui/GlassInput';
import { GlassSelect } from '../ui/GlassSelect';
import { KeyRound, Plus, Trash2, Copy, Check, AlertTriangle } from 'lucide-react';
import './AgentTokensPanel.css';

// Mirror of the backend scope catalogue (authorization.DEFAULT_MODULES_BY_TIER
// enterprise set). The backend validates scopes; this list only drives the picker.
const MODULE_KEYS: { key: string; label: string }[] = [
  { key: 'dashboard', label: 'Dashboard' },
  { key: 'brand', label: 'Brand' },
  { key: 'content', label: 'Content' },
  { key: 'campaigns', label: 'Campaigns' },
  { key: 'multichannel', label: 'Multi-Channel' },
  { key: 'scheduling', label: 'Scheduling' },
  { key: 'landing_pages', label: 'Landing Pages' },
  { key: 'seo', label: 'SEO' },
  { key: 'images', label: 'Images' },
  { key: 'lead_capture', label: 'Lead Capture' },
  { key: 'analytics', label: 'Analytics' },
  { key: 'websites', label: 'Websites' },
  { key: 'leads_crm', label: 'CRM / Leads' },
  { key: 'quizzes', label: 'Quizzes' },
  { key: 'scripts', label: 'Scripts' },
  { key: 'reviews', label: 'Reviews' },
  { key: 'wordpress', label: 'WordPress' },
  { key: 'edm', label: 'Email Marketing' },
  { key: 'sms', label: 'SMS' },
  { key: 'domains', label: 'Domains' },
  { key: 'optimization', label: 'Optimization' },
  { key: 'integrations', label: 'Integrations' },
];

const EXPIRY_OPTIONS = [
  { value: '30', label: '30 days' },
  { value: '90', label: '90 days (default)' },
  { value: '365', label: '1 year' },
];

const AgentTokensPanel: React.FC = () => {
  const [available, setAvailable] = useState<boolean>(true); // false when feature is dark (404)
  const [tokens, setTokens] = useState<AgentTokenMetadata[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Create form state
  const [showCreate, setShowCreate] = useState(false);
  const [name, setName] = useState('');
  const [readModules, setReadModules] = useState<Set<string>>(new Set());
  const [writeModules, setWriteModules] = useState<Set<string>>(new Set());
  const [allowGenerate, setAllowGenerate] = useState(false);
  const [expiryDays, setExpiryDays] = useState('90');
  const [creating, setCreating] = useState(false);

  // One-time plaintext reveal
  const [createdToken, setCreatedToken] = useState<AgentTokenCreateResponse | null>(null);
  const [copied, setCopied] = useState(false);

  const loadTokens = useCallback(async () => {
    try {
      setLoading(true);
      const list = await agentTokensApi.list();
      setTokens(list);
      setAvailable(true);
    } catch (err: any) {
      if (err?.response?.status === 404) {
        setAvailable(false); // feature dark — hide the whole section
      } else {
        setError('Failed to load API tokens');
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadTokens();
  }, [loadTokens]);

  const toggleSet = (set: Set<string>, key: string): Set<string> => {
    const next = new Set(set);
    if (next.has(key)) next.delete(key);
    else next.add(key);
    return next;
  };

  const resetForm = () => {
    setName('');
    setReadModules(new Set());
    setWriteModules(new Set());
    setAllowGenerate(false);
    setExpiryDays('90');
    setShowCreate(false);
  };

  const handleCreate = async () => {
    const scopes: string[] = [];
    readModules.forEach((m) => scopes.push(`${m}:read`));
    writeModules.forEach((m) => scopes.push(`${m}:write`));
    if (allowGenerate) scopes.push('generate');
    if (!name.trim() || scopes.length === 0) {
      setError('Give the token a name and at least one scope');
      return;
    }
    try {
      setCreating(true);
      setError(null);
      const expiresAt = new Date(Date.now() + parseInt(expiryDays, 10) * 24 * 60 * 60 * 1000).toISOString();
      const created = await agentTokensApi.create({ name: name.trim(), scopes, expires_at: expiresAt });
      setCreatedToken(created);
      setCopied(false);
      resetForm();
      await loadTokens();
    } catch (err: any) {
      setError(err?.response?.data?.detail || 'Failed to create token');
    } finally {
      setCreating(false);
    }
  };

  const handleRevoke = async (token: AgentTokenMetadata) => {
    if (!window.confirm(`Revoke "${token.name}"? Any agent using it will immediately lose access.`)) return;
    try {
      await agentTokensApi.revoke(token.id);
      await loadTokens();
    } catch {
      setError('Failed to revoke token');
    }
  };

  const handleCopy = async () => {
    if (!createdToken) return;
    try {
      await navigator.clipboard.writeText(createdToken.token);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // clipboard unavailable — user can select the text manually
    }
  };

  const formatDate = (iso: string | null) => (iso ? new Date(iso).toLocaleDateString() : '—');

  if (!available) return null;

  return (
    <GlassCard variant="default" padding="lg" className="profile-section-card agent-tokens-card">
      <div className="section-header">
        <h2>
          <KeyRound size={20} style={{ verticalAlign: 'middle', marginRight: 8 }} />
          API Tokens
        </h2>
        {!showCreate && (
          <GlassButton variant="primary" size="sm" onClick={() => setShowCreate(true)}>
            <Plus size={16} /> New Token
          </GlassButton>
        )}
      </div>
      <p className="agent-tokens-intro">
        Personal access tokens let an external AI agent (Claude Desktop, Claude Code, custom
        harnesses) work in AIME on your behalf. Tokens carry only the scopes you pick, never more
        than your account can already do, and can be revoked at any time.
      </p>

      {error && <div className="agent-tokens-error">{error}</div>}

      {/* One-time plaintext reveal */}
      {createdToken && (
        <div className="agent-token-reveal">
          <div className="agent-token-reveal-header">
            <AlertTriangle size={16} />
            <span>Copy this token now — you won't see it again.</span>
          </div>
          <div className="agent-token-reveal-row">
            <code className="agent-token-plaintext">{createdToken.token}</code>
            <GlassButton variant="secondary" size="sm" onClick={handleCopy}>
              {copied ? <Check size={16} /> : <Copy size={16} />} {copied ? 'Copied' : 'Copy'}
            </GlassButton>
          </div>
          <GlassButton variant="secondary" size="sm" onClick={() => setCreatedToken(null)}>
            Done
          </GlassButton>
        </div>
      )}

      {/* Create form */}
      {showCreate && (
        <div className="agent-token-create">
          <div className="agent-token-create-row">
            <GlassInput
              label="Token name"
              value={name}
              onChange={(value: string) => setName(value)}
              placeholder='e.g. "Claude Desktop — laptop"'
            />
            <GlassSelect
              label="Expires in"
              value={expiryDays}
              onChange={(value: string) => setExpiryDays(value)}
              options={EXPIRY_OPTIONS}
            />
          </div>

          <div className="agent-token-scopes">
            <div className="agent-token-scopes-head">
              <span className="agent-token-scopes-module">Module</span>
              <span>Read</span>
              <span>Write</span>
            </div>
            {MODULE_KEYS.map(({ key, label }) => (
              <div key={key} className="agent-token-scope-row">
                <span className="agent-token-scopes-module">{label}</span>
                <input
                  type="checkbox"
                  checked={readModules.has(key)}
                  onChange={() => setReadModules(toggleSet(readModules, key))}
                  aria-label={`${label} read`}
                />
                <input
                  type="checkbox"
                  checked={writeModules.has(key)}
                  onChange={() => setWriteModules(toggleSet(writeModules, key))}
                  aria-label={`${label} write`}
                />
              </div>
            ))}
          </div>

          <label className="agent-token-generate">
            <input
              type="checkbox"
              checked={allowGenerate}
              onChange={() => setAllowGenerate(!allowGenerate)}
            />
            Allow AI generation (billable — counts against your monthly AI quota)
          </label>

          <div className="agent-token-create-actions">
            <GlassButton variant="primary" onClick={handleCreate} disabled={creating}>
              {creating ? 'Creating…' : 'Create Token'}
            </GlassButton>
            <GlassButton variant="secondary" onClick={resetForm} disabled={creating}>
              Cancel
            </GlassButton>
          </div>
        </div>
      )}

      {/* Token list */}
      {loading ? (
        <p className="agent-tokens-empty">Loading…</p>
      ) : tokens.length === 0 && !showCreate ? (
        <p className="agent-tokens-empty">No API tokens yet.</p>
      ) : (
        <div className="agent-token-list">
          {tokens.map((t) => (
            <div key={t.id} className={`agent-token-row${t.revoked_at ? ' revoked' : ''}`}>
              <div className="agent-token-row-main">
                <span className="agent-token-name">{t.name}</span>
                <code className="agent-token-prefix">{t.token_prefix}…</code>
                {t.revoked_at && <span className="agent-token-status">Revoked</span>}
                {!t.revoked_at && t.expires_at && new Date(t.expires_at) < new Date() && (
                  <span className="agent-token-status">Expired</span>
                )}
              </div>
              <div className="agent-token-row-meta">
                <span>{t.scopes.length} scope{t.scopes.length === 1 ? '' : 's'}</span>
                <span>Last used: {formatDate(t.last_used_at)}</span>
                <span>Expires: {formatDate(t.expires_at)}</span>
              </div>
              {!t.revoked_at && (
                <GlassButton variant="secondary" size="sm" onClick={() => handleRevoke(t)}>
                  <Trash2 size={16} /> Revoke
                </GlassButton>
              )}
            </div>
          ))}
        </div>
      )}
    </GlassCard>
  );
};

export default AgentTokensPanel;
